"use client";

import { validatePresetKey } from "@/design-system/presets";
import { COLOR_THEME_CSS_STORAGE_KEY, COLOR_THEME_STORAGE_KEY, COLOR_THEME_STYLE_ID, type CustomThemeColors, resolvePresetStyleSheet } from "@/design-system/theme-storage";
import type { Surface } from "@/design-system/tone";
import type { PresetKey } from "@/lib/portal-theme/preset-keys";
/**
 * DisputeWell Design System — Unified Theme Provider
 *
 * Replaces CrmThemeProvider + PortalThemeProvider with ONE provider
 * that handles all theming contexts: CRM, portal, admin, fullscreen editors.
 *
 * How it works:
 *   1. Accepts a preset key (from presets.ts) or "custom" + explicit brand overrides
 *   2. Injects CSS variables via a <style> tag (`:root` for light, `.dw-dark` for dark)
 *   3. For "default" preset, removes overrides — globals.css handles defaults
 *   4. In interactive mode (CRM), provides setPreset() with localStorage mirroring
 *   5. In static mode (portal), key comes from server props only
 *
 * Usage:
 *   CRM:    <DesignSystemProvider presetKey={memberTheme} interactive>
 *   Portal: <DesignSystemProvider presetKey={portalTheme} customThemeColors={colors}>
 */

import { createContext, memo, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface DesignSystemContextValue {
  /** Current active preset key */
  presetKey: PresetKey;
  /** Change the active preset (only works in interactive mode). For "custom",
   *  pass the colors to wear; they replace the server-rendered custom colors
   *  until the next server render (the agent's own CRM theme, 2026-09-21). */
  setPreset: (key: PresetKey, customColors?: CustomThemeColors | null) => void;
  /** The product surface this provider was mounted for (blueprint §10.4).
   * Consumed by `useMicrocopy()` to resolve `SurfaceVariants` (bySurface)
   * microcopy leaves without every caller threading the surface manually. */
  surface: Surface;
}

const DesignSystemContext = createContext<DesignSystemContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Access the design system context. Throws if used outside provider. */
export function useDesignSystem(): DesignSystemContextValue {
  const ctx = useContext(DesignSystemContext);
  if (!ctx) {
    throw new Error("useDesignSystem must be used within DesignSystemProvider");
  }
  return ctx;
}

/** Optional access — returns null if outside provider. */
export function useDesignSystemOptional(): DesignSystemContextValue | null {
  return useContext(DesignSystemContext);
}

export const DesignSystemProvider = memo(function DesignSystemProvider(
  { children, presetKey: serverKey, customThemeColors, interactive = false, surface }: {
    children?: ReactNode;
    presetKey: string;
    customThemeColors?: {
      primary: string;
      accent: string;
      primaryDark?: string;
      accentDark?: string;
      background?: string;
      muted?: string;
      card?: string;
    } | null;
    interactive?: boolean;
    surface: Surface;
  }
) {
  const [activeKey, setActiveKey] = useState<PresetKey>(() => validatePresetKey(serverKey));
  // Custom colors chosen at runtime (undefined = none chosen; the server's apply).
  const [runtimeColors, setRuntimeColors] = useState<CustomThemeColors | null | undefined>(undefined);
  const effectiveCustomColors = runtimeColors === undefined ? customThemeColors : runtimeColors;

  const styleRef = useRef<HTMLStyleElement | null>(null);
  // OWNERSHIP FLAG (2026-08-03). The <style id={COLOR_THEME_STYLE_ID}> node can
  // arrive two ways: DesignSystemInitialStyle RENDERS it through React (SSR
  // paint, no flash), or this provider creates it when none exists. We may only
  // imperatively remove the one WE created — tearing out React's node leaves the
  // fiber pointing at a detached child, and the next unmount of the layout dies
  // with "removeChild ... not a child of this node". Harmless while the layout
  // never unmounts; fatal once anything unmounts it.
  const styleIsOursRef = useRef(false);

  useEffect(() => {
    setActiveKey(validatePresetKey(serverKey));
  }, [serverKey]);

  // Inject / update the <style> tag whenever the active preset changes
  useEffect(() => {
    const resolvedCss = resolvePresetStyleSheet(activeKey, effectiveCustomColors ?? null);
    const cachedCustomCss = interactive && activeKey === "custom" && typeof window !== "undefined" ? window.localStorage.getItem(COLOR_THEME_CSS_STORAGE_KEY) : null;
    const css = resolvedCss ?? cachedCustomCss;

    if (interactive && typeof window !== "undefined") {
      window.localStorage.setItem(COLOR_THEME_STORAGE_KEY, activeKey);
      if (resolvedCss) {
        window.localStorage.setItem(COLOR_THEME_CSS_STORAGE_KEY, resolvedCss);
      } else if (activeKey !== "custom") {
        window.localStorage.removeItem(COLOR_THEME_CSS_STORAGE_KEY);
      }
    }

    if (!css) {
      if (styleRef.current) {
        // Only detach a node we own; React's own node just gets emptied.
        if (styleIsOursRef.current) {
          styleRef.current.remove();
        } else {
          styleRef.current.textContent = "";
        }
        styleRef.current = null;
        styleIsOursRef.current = false;
      }
      return;
    }

    // Create or reuse the style element
    let el = styleRef.current;
    if (!el) {
      el = document.getElementById(COLOR_THEME_STYLE_ID) as HTMLStyleElement | null;
      // Adopted, not created — this is DesignSystemInitialStyle's React-owned
      // node. We may write its textContent, never remove it.
      if (el) {
        styleIsOursRef.current = false;
      }
    }
    if (!el) {
      el = document.createElement("style");
      el.id = COLOR_THEME_STYLE_ID;
      document.head.appendChild(el);
      styleIsOursRef.current = true;
    }
    el.textContent = css;
    styleRef.current = el;
  }, [activeKey, effectiveCustomColors, interactive]);

  // Full cleanup on unmount — same ownership rule. Removing React's node here is
  // what made exiting the portal editor throw NotFoundError once main-next's
  // ProtectedConvexAuthBoundary started unmounting the fullscreen subtree.
  useEffect(() => {
    return () => {
      if (styleRef.current) {
        if (styleIsOursRef.current) {
          styleRef.current.remove();
        }
        styleRef.current = null;
        styleIsOursRef.current = false;
      }
    };
  }, []);

  // Stamp the active preset onto <html> so a theme's sheet can re-bind the
  // fixed color tokens the preset injection leaves alone (Forest's and Mono's
  // status colors). A color theme changes colors only (ruled 2026-10-17).
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-preset", activeKey);
    }
  }, [activeKey]);

  const setPreset = useCallback(
    (key: PresetKey, customColors?: CustomThemeColors | null) => {
      if (!interactive) {
        return;
      } // No-op in static mode

      const validated = validatePresetKey(key);
      if (customColors !== undefined) {
        setRuntimeColors(customColors);
      }
      setActiveKey(validated);
      localStorage.setItem(COLOR_THEME_STORAGE_KEY, validated);
    },
    [interactive],
  );

  return (
    <DesignSystemContext.Provider value={{ presetKey: activeKey, setPreset, surface }} data-component="DesignSystemProvider" data-location="src/design-system/provider.tsx">
      {children}
    </DesignSystemContext.Provider>
  );
});
